<template>
  <b-modal
    v-model="form.status"
    :title="$t('guides.createTask.action')"
    size="lg"
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
    no-enforce-focus
  >
    <validation-observer ref="observer">
      <b-form @submit.prevent="createTask">
        <b-form-group
          :label="$t('guides.createTask.taskTitle')"
          label-for="task-title"
        >
          <validation-provider
            #default="{ errors }"
            :name="$t('guides.createTask.taskTitle')"
            vid="task-title"
            rules="required|min:3"
          >
            <b-form-input
              id="task-title"
              v-model="form.title"
              :placeholder="$t('guides.createTask.taskTitlePlaceholder')"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group
          :label="$t('fields.project')"
          label-for="task-project"
        >
          <v-select
            id="task-project"
            v-model="form.id_projects"
            :options="projectsList"
            label="title"
            :reduce="item => item.id"
            :placeholder="$t('guides.createTask.projectPlaceholder')"
            clearable
          />
        </b-form-group>
        <b-form-group
          :label="$t('guides.createTask.watcher')"
          label-for="task-manager"
        >
          <v-select
            id="task-manager"
            v-model="form.id_manager_users"
            :options="usersList"
            label="name"
            :reduce="item => item.id"
            :placeholder="$t('guides.createTask.watcherPlaceholder')"
            clearable
            multiple
          />
          <small
            class="text-muted"
            v-text="$t('guides.createTask.watcherHelp')"
          />
        </b-form-group>
        <b-form-group
          :label="$t('fields.department')"
          label-for="id_departments"
        >
          <v-select
            id="id_departments"
            v-model="form.id_departments"
            :options="departments"
            label="title"
            :reduce="item => item.id"
            :placeholder="$t('fields.selectDepartment')"
            clearable
            @input="setTeams"
          />
        </b-form-group>
        <b-form-group
          :label="$t('fields.team')"
          label-for="id_teams"
        >
          <v-select
            id="id_departments"
            v-model="form.id_teams"
            :options="teams"
            label="title"
            :reduce="item => item.id"
            :placeholder="$t('fields.selectTeam')"
            clearable
            :disabled="!form.id_departments"
          />
        </b-form-group>
        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="outline-secondary"
            class="mr-2"
            @click="form.status = false"
          >
            {{ $t('common.cancel') }}
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            :disabled="submitting"
          >
            {{ submitting ? $t('common.creating') : $t('common.create') }}
          </b-button>
        </div>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  BModal, BForm, BFormGroup, BFormInput, BButton,
} from 'bootstrap-vue'
import { required } from '@validations'
import vSelect from 'vue-select'
import { definitionId } from '@/libs/definitions'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'CreateTaskFromGuideModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  data() {
    return {
      required,
      submitting: false,
    }
  },
  computed: {
    form() {
      return this.$store.getters['guides/createTaskGuideModal']
    },
    projectsList() {
      return this.$store.getters['projects/dataList'] || []
    },
    departments() {
      return this.$store.getters['departments/dataList'] || []
    },
    teams() {
      return this.$store.getters['teams/dataList'] || []
    },
    usersList() {
      return this.$store.getters['users/dataList'] || []
    },
  },
  watch: {

  },
  created() {
    this.$store.commit('guides/RESET_CREATE_TASK_GUIDE_MODEL')
    this.loadData()
    this.setDepartments()
  },
  methods: {
    resetForm() {
      this.$store.commit('guides/RESET_CREATE_TASK_GUIDE_MODEL')
      this.form.title = this.form?.title ? `${this.form.title} (kopya)` : ''
      this.submitting = false
    },
    loadData() {
      this.$store.dispatch('projects/getDataList', {
        select: ['projects.id AS id', 'projects.title AS title'],
        where: { 'projects.id_project_statuses': definitionId('project', 'open') },
      })
      this.$store.dispatch('users/getDataList', {
        select: ['users.id AS id', 'users.name AS name'],
        where: { 'users.status': 1 },
      })
    },
    async createTask() {
      const valid = await this.$refs.observer.validate()
      if (!valid) return
      if (!this.form?.id_guides) {
        this.$toast({
          component: ToastificationContent,
          props: {
            variant: 'danger',
            title: this.$t('common.toast.errorShort'),
            icon: 'XIcon',
            text: this.$t('guides.createTask.missingGuide'),
          },
        })
        return
      }
      this.submitting = true
      try {
        const taskId = await this.$store.dispatch('guides/createTaskFromGuide', {
          id_guides: this.form.id_guides,
          title: this.form.title.trim(),
          id_projects: this.form.id_projects || null,
          id_manager_users: this.form.id_manager_users || null,
          id_departments: this.form.id_departments || null,
          id_teams: this.form.id_teams || null,
        })
        this.form.status = false
        this.$toast({
          component: ToastificationContent,
          props: {
            variant: 'success',
            title: this.$t('common.toast.successShort'),
            icon: 'CheckIcon',
            text: this.$t('guides.createTask.success'),
          },
        })
        await this.$store.dispatch('guides/closeCreateTaskModal')
        await this.$router.push(`/app/tasks/detail/${taskId}`)
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            variant: 'danger',
            title: this.$t('common.toast.errorShort'),
            icon: 'XIcon',
            text: e?.message || this.$t('guides.createTask.failed'),
          },
        })
      } finally {
        this.submitting = false
      }
    },
    setDepartments() {
      this.$store.dispatch('departments/getDataList', {
        select: [
          'departments.id AS id',
          'departments.title AS title',
        ],
        where: {
          'departments.status': 1,
        },
      })
    },
    setTeams() {
      this.$store.dispatch('teams/getDataList', {
        select: [
          'teams.id AS id',
          'teams.title AS title',
        ],
        where: {
          'teams.id_departments': this.form.id_departments,
        },
      })
    },
  },
}
</script>
